import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import styled from "styled-components";

const Overlay = styled(motion.div)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: ${({ theme }) => theme.zIndex.overlay};
  padding: ${({ theme }) => theme.spacing(2)};
  pointer-events: none;

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing(1)};
    padding-bottom: ${({ theme }) =>
      `calc(env(safe-area-inset-bottom, 0) + ${theme.spacing(1)})`};
  }
`;

const Banner = styled.div`
  pointer-events: auto;
  max-width: 720px;
  margin: 0 auto;
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(3.5)}`};
  background: ${({ theme }) => theme.colors.background.glass};
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid ${({ theme }) => theme.colors.background.glassBorder};
  border-radius: ${({ theme }) => `${theme.radius.lg}px`};
  box-shadow: ${({ theme }) => theme.colors.shadow.medium};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;

  @media (max-width: 600px) {
    flex-direction: column;
    text-align: center;
    padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(2)}`};
  }
`;

const Text = styled.p`
  margin: 0;
  flex: 1;
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;

  a {
    color: ${({ theme }) => theme.colors.text.link};
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover {
      color: ${({ theme }) => theme.colors.accent.primaryHover};
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  flex-shrink: 0;
`;

const BannerButton = styled.button`
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2.5)}`};
  border-radius: ${({ theme }) => `${theme.radius.sm}px`};
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 600;
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast} ease,
    transform ${({ theme }) => theme.motion.fast} ease;
  border: none;

  &:active {
    transform: scale(0.97);
  }
`;

const AcceptButton = styled(BannerButton)`
  background: ${({ theme }) => theme.colors.accent.primary};
  color: ${({ theme }) => theme.colors.text.inverse};

  &:hover {
    background: ${({ theme }) => theme.colors.accent.primaryHover};
  }
`;

const RejectButton = styled(BannerButton)`
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};

  &:hover {
    background: ${({ theme }) => theme.colors.state.hover};
  }
`;

const CookieConsent = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      setVisible(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem("cookieConsent", "accepted");
    setVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem("cookieConsent", "rejected");
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <Overlay
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
        >
          <Banner role="alert" aria-live="polite">
            <Text>
              {t(
                "cookies.message",
                "We use cookies to improve your experience. By continuing to browse, you agree to our use of cookies."
              )}{" "}
              <Link to="/privacy-policy">
                {t("cookies.learn_more", "Learn More")}
              </Link>
            </Text>
            <ButtonGroup>
              <AcceptButton onClick={handleAccept}>
                {t("cookies.accept", "Accept")}
              </AcceptButton>
              <RejectButton onClick={handleReject}>
                {t("cookies.reject", "Reject")}
              </RejectButton>
            </ButtonGroup>
          </Banner>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
